/**
 * Alpha Workwear - navigation progress bar.
 *
 * Shows a thin red bar at the top of the viewport while the browser is fetching
 * the next page, so a visitor on a poor connection gets an immediate answer to
 * "did that tap do anything".
 *
 * The whole file is best-effort. If it never runs, every link still works and
 * the shop looks exactly as it does today - there is no markup that depends on
 * it and nothing is hidden waiting for it.
 *
 * Three rules shape what is in here:
 *
 *   NEVER COVER CONTENT. No splash, no veil, no scroll lock. The bar is 3px and
 *   pointer-events: none. A loading screen that hides the shop is worse than no
 *   loading screen at all - if this script throws halfway, the visitor is left
 *   staring at an overlay with no way past it.
 *
 *   NEVER FLASH ON A FAST PAGE. Nothing is shown for the first 140ms. Most
 *   navigations on a decent connection finish inside that, and a bar that
 *   appears and vanishes in two frames reads as a glitch, not as progress.
 *
 *   NEVER LIE ABOUT BEING FINISHED. Real progress is unknowable - the browser
 *   does not tell a page how far along the NEXT document is - so the bar eases
 *   towards 90% and waits there. It only completes when the old page is
 *   actually being torn down (pagehide), which is the one moment we know for
 *   certain that the new one has arrived.
 */

(function () {
  var START_DELAY = 140;
  var CREEP_INTERVAL = 220;
  var CEILING = 0.9;

  var el = null;
  var bar = null;
  var startTimer = null;
  var creepTimer = null;
  var progress = 0;
  var running = false;

  function build() {
    if (el) return el;
    el = document.createElement('div');
    el.className = 'alpha-loader';
    // Decorative in the sense that it duplicates state the browser already
    // reports through its own loading UI; announcing it would interrupt a
    // screen reader mid-sentence for something it cannot act on.
    el.setAttribute('aria-hidden', 'true');
    /*
     * A <span>, not a <div>.
     *
     * base.css carries `div:empty { display: none }`, and this element is empty
     * by design - it is a coloured rectangle, there is nothing to put inside it.
     * As a div it was display:none from the moment it was created: the inline
     * scaleX was applied on schedule, computed to `none`, and the bar measured
     * 0px wide while faithfully reporting progress nobody could see. Same trap
     * that forced .alpha-header__spacer to be a span.
     */
    bar = document.createElement('span');
    bar.className = 'alpha-loader__bar';
    el.appendChild(bar);
    document.body.appendChild(el);
    return el;
  }

  function paint() {
    if (bar) bar.style.transform = 'scaleX(' + progress + ')';
  }

  function start() {
    if (running) return;
    running = true;
    window.clearTimeout(startTimer);

    startTimer = window.setTimeout(function () {
      if (!running) return;
      build();
      progress = 0.08;
      paint();
      el.classList.add('is-active');

      // Ease towards the ceiling in ever-smaller steps, so the bar keeps moving
      // on a slow connection without ever reaching the end and implying the
      // page is ready when it is not.
      creepTimer = window.setInterval(function () {
        if (!running) return;
        progress += (CEILING - progress) * 0.18;
        if (progress > CEILING) progress = CEILING;
        paint();
      }, CREEP_INTERVAL);
    }, START_DELAY);
  }

  function done() {
    window.clearTimeout(startTimer);
    window.clearInterval(creepTimer);
    if (!running) return;
    running = false;

    // Never shown (fast navigation) - just reset and leave no trace.
    if (!el || !el.classList.contains('is-active')) {
      progress = 0;
      paint();
      return;
    }

    progress = 1;
    paint();
    window.setTimeout(function () {
      if (running || !el) return;
      el.classList.remove('is-active');
      // Reset only after the fade has finished, or the bar visibly snaps back
      // to zero while it is still on screen.
      window.setTimeout(function () {
        if (running) return;
        progress = 0;
        paint();
      }, 220);
    }, 180);
  }

  /*
   * Which clicks mean "a new document is coming".
   *
   * Everything excluded here either does not navigate at all or is handled by
   * JavaScript that keeps the visitor on the page - starting the bar for those
   * would leave it stuck at 90% with nothing ever arriving to finish it.
   */
  function navigates(link, event) {
    if (!link || !link.href) return false;
    if (event.defaultPrevented) return false;
    // A modified click opens a new tab or downloads - this page is not going
    // anywhere, so nothing should be reported on it.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (link.target && link.target !== '_self') return false;
    if (link.hasAttribute('download')) return false;

    var proto = link.protocol;
    if (proto !== 'http:' && proto !== 'https:') return false;
    if (link.host !== window.location.host) return false;

    // Same page, different anchor: no document is fetched.
    if (link.pathname === window.location.pathname && link.search === window.location.search && link.hash) {
      return false;
    }

    // Anything the theme handles in place. The facet forms, the cart drawer and
    // the quick-add modal all fetch sections over AJAX and never unload the
    // document, so the bar would start and never be finished.
    if (link.closest('cart-drawer, facet-filters-form, .alpha-coll__toolbar, quick-add-modal, predictive-search')) {
      return false;
    }

    return true;
  }

  /*
   * BUBBLE phase, deliberately.
   *
   * On capture this fired before any other handler had run, so a link whose own
   * handler cancels the click - and this theme is full of them - started a bar
   * for a navigation that never happened. Nothing then arrives to finish it, so
   * it creeps to 90% and sits there for the rest of the visit. Measured exactly
   * that: 0.89 and frozen. By the time the event bubbles back up here every
   * other handler has had its say, so `defaultPrevented` is trustworthy.
   */
  document.addEventListener('click', function (event) {
    var link = event.target && event.target.closest ? event.target.closest('a') : null;
    if (navigates(link, event)) start();
  });

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || event.defaultPrevented) return;
    // Same reasoning as the links: the theme's own AJAX forms stay put.
    if (form.closest('cart-drawer, facet-filters-form, .alpha-coll__toolbar, predictive-search')) return;
    if (form.getAttribute('action') && form.getAttribute('action').indexOf('/cart/') === 0) return;
    start();
  });

  // The one reliable "the next page is here" signal available to the outgoing
  // document. Firing `done` rather than just hiding keeps the bar completing
  // rather than blinking out.
  window.addEventListener('pagehide', done);

  /*
   * Coming BACK to this page from the back/forward cache. The document is
   * restored exactly as it was left - which includes a bar frozen at 90% from
   * the navigation that took the visitor away - so it has to be cleared.
   */
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      running = false;
      done();
      if (el) {
        el.classList.remove('is-active');
        progress = 0;
        paint();
      }
    }
  });

  // A cancelled navigation (server refused, download instead of a page, the
  // visitor hit stop) leaves the bar running with no pagehide ever arriving.
  // Give up after 20s rather than creep at 90% forever.
  window.addEventListener('beforeunload', function () {
    window.setTimeout(done, 20000);
  });
})();
